import {
	CdkDragDrop,
	moveItemInArray,
	transferArrayItem,
} from '@angular/cdk/drag-drop';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	Inject,
	OnInit,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import { ICON } from '@core/util/icon';
import {
	BusinessChecklistVM,
	ChecklistStageVM,
	ChecklistTaskVM,
} from '@models/checklist';
import { MatterFileType } from '@models/matter';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SnackbarService } from '@services/ui/snackbar.service';
import {
	BusinessChecklistsDispatch,
	BusinessChecklistsSelectors,
} from '@state/business/checklists';
import { cloneDeep } from 'lodash';
import { nanoid } from 'nanoid/non-secure';

interface AddTemplateDialogData {
	selectedChecklist?: BusinessChecklistVM;
	editing?: boolean;
	defaultFileType?: MatterFileType;
}

interface StageVM extends ChecklistStageVM {
	open: boolean;
}

@Component({
	selector: 'app-add-template-dialog',
	templateUrl: './add-template-dialog.component.html',
	styleUrls: ['./add-template-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	animations: [openClose()],
	standalone: false,
})
export class AddTemplateDialogComponent implements OnInit {
	public step = 1;
	public ICON = ICON;
	public templateName: string;
	public selectedFileType: MatterFileType = MatterFileType.SALE;
	public MatterFileType = MatterFileType;
	public stages: StageVM[] = [];
	public editingStageIndex = -1;
	public editingTaskIndex = -1;
	public selectedStageIndex = 0;
	public selectedChecklist: BusinessChecklistVM;
	public editable = true;
	public editing = true;
	public default = false;
	public itemsToDelete: string[] = [];

	@SelectSnapshot(BusinessChecklistsSelectors.getDefaultChecklistSaleVM)
	defaultChecklistSale: BusinessChecklistVM;
	@SelectSnapshot(BusinessChecklistsSelectors.getDefaultChecklistPurchaseVM)
	defaultChecklistPurchase: BusinessChecklistVM;
	@SelectSnapshot(BusinessChecklistsSelectors.getDefaultChecklistRefinanceVM)
	defaultChecklistRefinance: BusinessChecklistVM;

	constructor(
		private dialogRef: MatDialogRef<AddTemplateDialogComponent>,
		private checklistDispatch: BusinessChecklistsDispatch,
		private snackbarService: SnackbarService,
		@Inject(MAT_DIALOG_DATA) public data: AddTemplateDialogData,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		this.selectedChecklist = this.data?.selectedChecklist;

		if (this.selectedChecklist) {
			this.editing = !!this.data?.editing;
			this.templateName = this.selectedChecklist.title;
			this.selectedFileType = this.selectedChecklist.fileType;
			this.default = this.selectedChecklist.default;
			this.stages = cloneDeep(this.selectedChecklist.stages).map(
				(stage, index) => ({
					...stage,
					open: index === 0,
				}),
			);
			this.step = this.editing ? 1 : 2;
			this.editable = !this.selectedChecklist.locked;
		} else if (this.data?.defaultFileType) {
			this.selectFileType(this.data.defaultFileType);
		}
	}

	close(): void {
		this.dialogRef.close();
	}

	nextStep(form: NgForm): void {
		if (form.valid) {
			this.step++;
		}
	}

	previousStep(): void {
		this.step--;
	}

	selectFileType(fileType: MatterFileType): void {
		this.selectedFileType = fileType;
	}

	create(draft: boolean): void {
		const newTemplate: BusinessChecklistVM = {
			id: this.selectedChecklist?.id ? this.selectedChecklist.id : nanoid(8),
			title: this.templateName,
			fileType: this.selectedFileType,
			draft: draft,
			created: this.selectedChecklist?.created
				? this.selectedChecklist.created
				: new Date().toISOString(),
			lastEdited: new Date().toISOString(),
			locked: false,
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			stages: this.stages.map(({ open, ...stage }) => ({
				...stage,
				tasks: stage.tasks.filter((task) => task.title !== ''),
			})),
			default: this.default,
		};

		const oldItemIds = [];

		if (this.selectedChecklist) {
			const oldTemplateId = this.selectedChecklist.id;
			const oldTasksId = [];
			const oldStagesIds = this.selectedChecklist.stages.map((s) => {
				oldTasksId.push(...s.tasks.map((t) => t.id));

				return s.id;
			});

			oldItemIds.push(oldTemplateId, ...oldStagesIds, ...oldTasksId);
		}

		const dedupedOldItemIds = Array.from(
			new Set([...oldItemIds, ...this.itemsToDelete]),
		);

		this.checklistDispatch.Create(newTemplate, dedupedOldItemIds);

		/**
		 * This logic should live in the backend and not in the frontend. Will need to clean this up.
		 */

		if (newTemplate.default && !draft) {
			switch (newTemplate.fileType) {
				case MatterFileType.SALE:
					if (
						!this.defaultChecklistSale?.locked &&
						this.defaultChecklistSale?.id !== newTemplate.id
					) {
						this.checklistDispatch.Create({
							...this.defaultChecklistSale,
							default: false,
						});
					}
					break;
				case MatterFileType.PURCHASE:
					if (
						!this.defaultChecklistPurchase?.locked &&
						this.defaultChecklistPurchase?.id !== newTemplate.id
					) {
						this.checklistDispatch.Create({
							...this.defaultChecklistPurchase,
							default: false,
						});
					}
					break;
				case MatterFileType.REFINANCE:
					if (
						!this.defaultChecklistRefinance?.locked &&
						this.defaultChecklistRefinance?.id !== newTemplate.id
					) {
						this.checklistDispatch.Create({
							...this.defaultChecklistRefinance,
							default: false,
						});
					}
					break;
			}
		}

		this.close();
	}

	handleAddTitle(type: 'stage' | 'task'): void {
		if (type === 'stage') {
			this.stages.forEach((stage) => {
				if (!stage.title) {
					stage.title = 'Untitled stage';
				}
			});
		} else {
			this.stages.forEach((stage) => {
				stage.tasks.forEach((task) => {
					if (!task.title) {
						task.title = 'Untitled task';
					}
				});
			});
		}
	}

	addStage(): void {
		this.editingStageIndex = this.stages.length;
		this.selectedStageIndex = this.editingStageIndex;
		this.handleAddTitle('stage');
		this.stages.push({
			id: nanoid(8),
			title: '',
			tasks: [],
			open: true,
		});
	}

	createTask(): ChecklistTaskVM {
		return {
			id: nanoid(8),
			title: '',
			complete: false,
		};
	}

	createStage(includeTask: boolean, stageName?: string): StageVM {
		return {
			id: nanoid(8),
			title: stageName ? stageName : '',
			tasks: includeTask ? [this.createTask()] : [],
			open: true,
		};
	}

	addTask(stageIndex?: number): void {
		this.handleAddTitle('task');
		if (this.stages.length === 0) {
			this.stages = [this.createStage(true, 'Untitled stage')];
			this.editingStageIndex = -1;
			this.editingTaskIndex = 0;
			this.selectedStageIndex = 0;
		} else if (stageIndex === undefined) {
			this.stages[this.selectedStageIndex].tasks.push(this.createTask());
			this.editingTaskIndex =
				this.stages[this.selectedStageIndex].tasks.length - 1;
		} else {
			this.editingTaskIndex = this.stages[stageIndex].tasks.length;
			this.selectedStageIndex = stageIndex;
			this.stages[stageIndex].tasks.push(this.createTask());
		}
	}

	editStageTitle(stage: StageVM, newTitle: string): void {
		stage.title = newTitle;
		this.editingStageIndex = this.stages.indexOf(stage);
		this.selectedStageIndex = this.editingStageIndex;
	}

	editTaskTitle(stage: StageVM, task: ChecklistTaskVM, newTitle: string): void {
		task.title = newTitle;
		this.editingTaskIndex = stage.tasks.indexOf(task);
		this.selectedStageIndex = this.stages.indexOf(stage);
	}

	resetEditingStageIndex(): void {
		this.editingStageIndex = -1;
		this.handleAddTitle('stage');
	}

	onInputStageBlur(): void {
		this.resetEditingStageIndex();
	}

	onInputTaskBlur(): void {
		const index = this.editingTaskIndex;
		setTimeout(() => {
			this.resetEditingTaskIndex('blur', index);
		}, 100);
	}

	resetEditingTaskIndex(
		type?: 'blur' | 'enter',
		currentEditingIndex?: number,
	): void {
		let editingNewTask = false;

		if (type !== 'blur') {
			this.handleAddTitle('task');
		}
		//find any empty tasks and remove them
		else if (
			this.stages[this.selectedStageIndex].tasks[this.editingTaskIndex]
				.title === '' &&
			this.isEditingTaskIndex(currentEditingIndex)
		) {
			this.deleteTask(this.selectedStageIndex, this.editingTaskIndex);
		} else if (
			this.stages[this.selectedStageIndex].tasks[currentEditingIndex].title ===
				'' &&
			currentEditingIndex !== this.editingTaskIndex
		) {
			editingNewTask = true;
			this.deleteTask(this.selectedStageIndex, currentEditingIndex, false);
		}

		const isLastTaskInStage =
			this.stages[this.selectedStageIndex].tasks.length - 1 ===
			this.editingTaskIndex;
		if (isLastTaskInStage && type !== 'blur') {
			this.addTask();
		} else if (
			!editingNewTask &&
			(currentEditingIndex === undefined ||
				currentEditingIndex === this.editingTaskIndex)
		) {
			this.editingTaskIndex = -1;
		}

		this.cdr.detectChanges();
	}

	edit(): void {
		if (this.editable) {
			this.editing = true;
			this.step = 1;
		}
	}

	duplicate(): void {
		const newTemplate: BusinessChecklistVM = {
			...this.selectedChecklist,
			id: nanoid(8),
			draft: true,
			default: false,
			locked: false,
			title: `${this.selectedChecklist.title} (copy)`,
			created: new Date().toISOString(),
			lastEdited: new Date().toISOString(),
		};

		this.checklistDispatch.Create(newTemplate);

		this.snackbarService.openSnackBar({
			text: 'Checklist template successfully duplicated. Ready to edit!',
			type: 'success',
		});

		this.close();
	}

	editStage(index: number): void {
		this.editingStageIndex = index;
		this.selectedStageIndex = index;
	}

	deleteStage(index: number): void {
		this.itemsToDelete.push(this.stages[index].id);
		this.stages.splice(index, 1);
		this.editingStageIndex = -1;
		this.selectedStageIndex = 0;
	}

	duplicateStage(stage: StageVM): void {
		const newStage: StageVM = {
			...stage,
			title: `${stage.title} (copy)`,
			open: true,
		};
		this.stages.push(newStage);
		this.editingStageIndex = this.stages.length - 1;
		this.selectedStageIndex = this.editingStageIndex;
	}

	editTask(stageIndex: number, taskIndex: number): void {
		this.editingTaskIndex = taskIndex;
		this.selectedStageIndex = stageIndex;
	}

	deleteTask(stageIndex: number, taskIndex: number, resetIndex = true): void {
		this.itemsToDelete.push(this.stages[stageIndex].tasks[taskIndex].id);
		this.stages[stageIndex].tasks.splice(taskIndex, 1);
		if (resetIndex) {
			this.editingTaskIndex = -1;
		}
		this.selectedStageIndex = stageIndex;
	}

	duplicateTask(stageIndex: number, task: ChecklistTaskVM): void {
		const newTask: ChecklistTaskVM = {
			...task,
			title: `${task.title} (copy)`,
			fieldPath: null,
		};
		this.stages[stageIndex].tasks.push(newTask);
		this.editingTaskIndex = this.stages[stageIndex].tasks.length - 1;
		this.selectedStageIndex = stageIndex;
	}

	toggleOpen(stageIndex: number): void {
		this.stages[stageIndex].open = !this.stages[stageIndex].open;
	}

	toggleDefault(event: boolean): void {
		this.default = event;
	}

	dropStage(event: CdkDragDrop<StageVM>) {
		const { previousIndex, currentIndex } = event;

		if (previousIndex === currentIndex) return;

		const selectedStageId = this.stages[this.selectedStageIndex].id;
		moveItemInArray(this.stages, previousIndex, currentIndex);
		this.selectedStageIndex = this.stages.findIndex(
			(s) => s.id === selectedStageId,
		);
	}

	stageTracker(index: number, stage: StageVM) {
		return `${index}-${stage.id}`;
	}

	getStageId(_stageIndex: number, stage: StageVM): string {
		return stage.id;
	}

	getConnectedStagesIds(currentStageIndex: number): string[] {
		const currentStageId = this.stages[currentStageIndex].id;
		const connectedStageIds = this.stages.reduce((acc, stage) => {
			if (currentStageId !== stage.id) {
				acc.push(stage.id);
			}

			return acc;
		}, [] as string[]);

		return connectedStageIds;
	}

	dropTask(event: CdkDragDrop<ChecklistTaskVM[]>) {
		const previousTaskIndex = event.previousIndex;
		const currentTaskIndex = event.currentIndex;
		const previousStageId = event.previousContainer.id;
		const currentStageId = event.container.id;

		if (
			previousStageId === currentStageId &&
			previousTaskIndex === currentTaskIndex
		)
			return;

		const previousStageIndex = this.stages.findIndex(
			(s) => previousStageId === s.id,
		);
		const currentStageIndex = this.stages.findIndex(
			(s) => currentStageId === s.id,
		);

		if (currentStageIndex === previousStageIndex) {
			moveItemInArray(
				this.stages[currentStageIndex].tasks,
				previousTaskIndex,
				currentTaskIndex,
			);
		} else {
			transferArrayItem(
				this.stages[previousStageIndex].tasks,
				this.stages[currentStageIndex].tasks,
				previousTaskIndex,
				currentTaskIndex,
			);
		}
	}

	taskTracker(index: number, task: ChecklistTaskVM) {
		return `${index}-${task.id}`;
	}

	checkIfTaskExists(): boolean {
		return this.stages.some(
			(stage) =>
				stage.tasks.length > 0 && stage.tasks.some((task) => task.title !== ''),
		);
	}

	private isEditingTaskIndex(currentEditingIndex: number) {
		return (
			currentEditingIndex === undefined ||
			currentEditingIndex === this.editingTaskIndex
		);
	}
}
